import { createNestablePublicClientApplication, InteractionRequiredAuthError, Configuration } from "@azure/msal-browser";
import { CLIENT_ID } from "../config";

import { getFallbackLoginPath } from "./utils";

// Note for NAA (Nested App Authentication) App Registration needs to be customomised:
//Add SPA redirect URI: brk-multihub://<appdomain> e.g. brk-multihub://app.fileops.com
//Add authorized client application: <client_id_of_office_host_app_registration> i.e itself as a client application and grant it "access_as_user" scope
let homeAccountId = null;
let loginDialog = null;
const unHandledErrorMessage = "Something went wrong with authentication or the authorization of the web application.";

const getAccessScope = () => {
  const origin = window.location.origin;

  switch (origin) {
    case "https://localhost:3000":
    case "https://dev.fileops.com":
    case "https://test.fileops.com":
    case "https://demo.fileops.com":
      return `api://${window.location.host}/${CLIENT_ID}/access_as_user`;
    case "https://appstaging.fileops.com":
    case "https://app.fileops.com":
      return `api://${window.location.host}/addin/${CLIENT_ID}/access_as_user`;
  }
};

const accessScope = getAccessScope();

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    //clientCapabilities are used to indicate to the Microsoft Identity platform that the application is capable of handling certain conditional access challenges, 
    // such as MFA or claims challenges. By including "CP1" in the clientCapabilities array, 
    // the application signals that it can handle these challenges, which can help prevent unnecessary authentication failures and improve the user experience.
    clientCapabilities: ["CP1"],
    authority: "https://login.microsoftonline.com/common", // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: getFallbackLoginPath(), // You must register this URI on Azure Portal/App Registration. Defaults to window.location.href
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true,
  },
};

let myMSALObj: Awaited<ReturnType<typeof createNestablePublicClientApplication>>;
let msalInitPromise: Promise<void> | null = null;

const initializeMSAL = (): Promise<void> => {
  if (!msalInitPromise) {
    // NAA requires Office to be ready before MSAL is initialized
    msalInitPromise = Office.onReady()
      .then(() => createNestablePublicClientApplication(msalConfig))
      .then(instance => {
        myMSALObj = instance;
      });
  }
  return msalInitPromise;
};

initializeMSAL().catch((error) => {
  console.error("Error initializing MSAL:", error);
});

export const getGraphToken = async (): Promise<string> => {
  await initializeMSAL()
  const result = await myMSALObj.acquireTokenSilent({
    scopes: ['https://graph.microsoft.com/Mail.Read', 'https://graph.microsoft.com/Mail.ReadWrite'],
  })
  return result.accessToken
}


export const getNAAToken = async (): Promise<string> => {
  await initializeMSAL();
  const result = await myMSALObj.acquireTokenSilent({
    scopes: [accessScope],
  });
  return result.accessToken;
};

// Attempts silent token acquisition only — never opens a dialog.
// Throws if silent acquisition fails so the caller can decide whether to show a login UI.
export const getFallbackToken = async (): Promise<string> => {
  await initializeMSAL();

  try {
    const result = await myMSALObj.acquireTokenSilent({
      scopes: [accessScope],
    });
    if (result !== null && result.accessToken !== null) {
      return result.accessToken;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError && error.claims) {
      // Claims challenge — retry silent with claims before giving up.
      try {
        const result = await myMSALObj.acquireTokenSilent({
          scopes: [accessScope],
          claims: error.claims,
        });
        if (result !== null && result.accessToken !== null) {
          return result.accessToken;
        }
      } catch {
        // fall through
      }
    }
    throw error;
  }

  throw new Error("Silent token acquisition returned no token");
};

export const fallbackLoginModal = async (claims?: string): Promise<string> => {
  if (loginDialog) {
    try { loginDialog.close(); } catch { /* already closed */ }
    loginDialog = null;
  }

  return new Promise((resolve, reject) => {
    const base = getFallbackLoginPath()
    const fullUrl = claims ? `${base}?claims=${encodeURIComponent(claims)}` : base

    // height and width are percentages of the size of the parent Office application, e.g., PowerPoint, Excel, Word, etc.
    Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30 }, function (result) {
      if (result.status === Office.AsyncResultStatus.Failed) {
        const errCode = result.error?.code;
        const errMsg = result.error?.message ?? "Login dialog could not be opened";
        // 12007 = dialog already open; 12009 = user dismissed the dialog prompt
        reject(new Error(`Login dialog failed (${errCode}): ${errMsg}`));
        return;
      }

      loginDialog = result.value;

      loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, function processMessage(arg) {
        const messageFromDialog = JSON.parse(arg.message);

        if (messageFromDialog.status === "success") {
          // Track the account id for future silent token requests.
          homeAccountId = messageFromDialog.accountId;

          // Configure MSAL to use the signed-in account as the active account for future requests.
          const homeAccount = myMSALObj.getAccount({ homeAccountId: messageFromDialog.accountId });

          myMSALObj.setActiveAccount(homeAccount);

          const accessToken = messageFromDialog.result;

          resolve(accessToken);
        } else {
          loginDialog.close();
          reject(unHandledErrorMessage);
        }

        loginDialog.close();
      });
    });
  });
};
